Player Inventory

<script setup>
/* eslint-disable */
import { ref, computed, onMounted } from 'vue'
import { useGameStore } from '@/store/game'
import { PrizeManager } from '@/services/prizeManager'
import { applyThemeClasses } from '@/utils/themeColors'

const props = defineProps({
  onClose: {
    type: Function,
    required: true
  }
})

const gameStore = useGameStore()

const activeTab = ref('all')

const tabOptions = [
  { id: 'all', label: 'All Items', icon: '📦' },
  { id: 'trophies', label: 'Trophies', icon: '🏆' },
  { id: 'consumables', label: 'Consumables', icon: '🌾' },
  { id: 'equipment', label: 'Equipment', icon: '✨' }
]

onMounted(() => {
  
});

const categorizedItems = computed(() => {
  if (!gameStore.playerOwner.inventory) {
    return {
      trophies: [],
      consumables: [],
      equipment: [],
      all: []
    };
  }

  const trophies = PrizeManager.getTrophyItems(gameStore.playerOwner.inventory);
  const consumables = PrizeManager.getConsumableItems(gameStore.playerOwner.inventory);
  const equipment = PrizeManager.getEquipmentItems(gameStore.playerOwner.inventory);

  return {
    trophies,
    consumables,
    equipment,
    all: [...trophies, ...consumables, ...equipment]
  };
})

const groupedTrophies = computed(() => {
  const trophies = categorizedItems.value.trophies;
  
  const groups = trophies.reduce((groups, item) => {
    const year = item.year;
    if (!groups[year]) {
      groups[year] = {
        monthly: [],
        yearly: []
      };
    }

    if (item.type === 'yearly') {
      groups[year].yearly.push(item);
    } else {
      groups[year].monthly.push(item);
    }

    return groups;
  }, {});

  return groups;
})

const yearsList = computed(() => {
  const years = Object.keys(groupedTrophies.value).sort((a, b) => b - a);
  return years;
})

const selectedYear = ref(yearsList.value ? yearsList.value[0] : null)

const getMonthName = (month) => {
  const months = [
    'January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'
  ]
  return months[month - 1] || 'Unknown'
}

const formatTime = (time) => {
  if (!time) return 'No time recorded'
  const minutes = Math.floor(time / 60)
  const seconds = Math.floor(time % 60)
  const milliseconds = Math.floor((time % 1) * 1000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`
}
</script>

<template>
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div :class="`rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4 ${applyThemeClasses('layout.card')}`">
      <div class="p-6">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
          <h2 :class="`text-2xl font-bold ${applyThemeClasses('text.primary')}`">Inventory</h2>
          <button 
            @click="$emit('close')"
            :class="`hover:opacity-70 ${applyThemeClasses('text.secondary')}`"
          >
            ✕
          </button>
        </div>

        <!-- Tab Navigation -->
        <div :class="`mb-6 border-b ${applyThemeClasses('layout.border')}`">
          <div class="flex space-x-4">
            <button
              v-for="tab in tabOptions"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'px-4 py-2 font-medium transition-colors',
                activeTab === tab.id
                  ? `text-blue-600 border-b-2 border-blue-600`
                  : `${applyThemeClasses('text.secondary')} hover:opacity-80`
              ]"
            >
              <span class="mr-2">{{ tab.icon }}</span>
              {{ tab.label }}
              <span class="ml-2 text-sm">
                ({{ categorizedItems[tab.id].length }})
              </span>
            </button>
          </div>
        </div>

        <!-- Year Selection (for trophies tab) -->
        <div v-if="activeTab === 'trophies' && yearsList && yearsList.length > 0" class="mb-6">
          <div class="flex justify-center space-x-2">
            <button
              v-for="year in yearsList"
              :key="year"
              @click="selectedYear = year"
              :class="[
                'px-4 py-2 rounded-lg',
                selectedYear === year
                  ? applyThemeClasses('button.primary')
                  : `${applyThemeClasses('layout.secondaryBackground')} hover:opacity-80`
              ]"
            >
              εγλ {{ year }}
            </button>
          </div>
        </div>

        <!-- Content Area -->
        <div class="min-h-[400px]">
          <!-- Trophies Tab -->
          <div v-if="activeTab === 'trophies'">
            <div v-if="groupedTrophies[selectedYear]">
              <!-- Monthly Trophies -->
              <div class="mb-8">
                <h3 :class="`text-xl font-semibold mb-4 ${applyThemeClasses('text.primary')}`">Monthly Championships</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    v-for="trophy in groupedTrophies[selectedYear].monthly"
                    :key="`${trophy.month}-${trophy.name}`"
                    :class="`border rounded-lg p-4 ${applyThemeClasses('layout.specialCard')} ${applyThemeClasses('layout.border')}`"
                  >
                    <div class="flex items-start gap-4">
                      <div class="text-4xl">🏆</div>
                      <div>
                        <h4 :class="`font-bold ${applyThemeClasses('text.primary')}`">{{ trophy.name }}</h4>
                        <p :class="`text-sm ${applyThemeClasses('text.secondary')}`">{{ trophy.description }}</p>
                        <div class="mt-2 space-y-1">
                          <p class="text-sm">
                            <span class="font-medium">Month:</span> 
                            {{ getMonthName(trophy.month) }}
                          </p>
                          <p class="text-sm">
                            <span class="font-medium">Winner:</span> 
                            {{ trophy.chocobo }}
                          </p>
                          <p class="text-sm">
                            <span class="font-medium">Time:</span> 
                            {{ formatTime(trophy.time) }}
                          </p>
                          <p class="text-sm text-green-600 font-medium">
                            Prize: {{ trophy.value.toLocaleString() }}G
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div 
                v-if="groupedTrophies[selectedYear].monthly.length === 0"
                :class="`text-center py-12 ${applyThemeClasses('text.secondary')}`"
              >
                No trophies won this year
              </div>
            </div>

            <!-- No Trophies State -->
            <div 
              v-if="categorizedItems.trophies.length === 0"
              :class="`text-center py-12 ${applyThemeClasses('text.secondary')}`"
            >
              No trophies earned yet
            </div>
          </div>

          <!-- Consumables Tab -->
          <div v-if="activeTab === 'consumables'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="item in categorizedItems.consumables"
              :key="item.instanceId"
              :class="`border rounded-lg p-4 hover:shadow-lg transition-shadow ${applyThemeClasses('layout.card')} ${applyThemeClasses('layout.border')}`"
              :style="{ borderColor: PrizeManager.getRarityColor(item.rarity) }"
            >
              <div class="flex items-start gap-3">
                <div class="text-2xl">{{ PrizeManager.getItemIcon(item) }}</div>
                <div class="flex-1">
                  <h4 :class="`font-bold ${applyThemeClasses('text.primary')}`">{{ item.name }}</h4>
                  <span 
                    :style="{ color: PrizeManager.getRarityColor(item.rarity) }"
                    class="text-xs font-medium uppercase"
                  >
                    {{ item.rarity }}
                  </span>
                  <p :class="`text-sm mt-1 ${applyThemeClasses('text.secondary')}`">{{ item.description }}</p>
                  <div class="mt-2 flex justify-between items-center">
                    <span :class="`text-sm ${applyThemeClasses('text.secondary')}`">Value: {{ item.value.toLocaleString() }}G</span>
                    <button
                      :class="`text-sm px-3 py-1 rounded ${applyThemeClasses('button.primary')}`"
                      @click="$emit('use-item', item)"
                    >
                      Use
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div 
              v-if="categorizedItems.consumables.length === 0"
              :class="`col-span-full text-center py-12 ${applyThemeClasses('text.secondary')}`"
            >
              No consumable items in inventory
            </div>
          </div>

          <!-- Equipment Tab -->
          <div v-if="activeTab === 'equipment'" class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              v-for="item in categorizedItems.equipment"
              :key="item.instanceId"
              :class="`border rounded-lg p-4 hover:shadow-lg transition-shadow ${applyThemeClasses('layout.card')} ${applyThemeClasses('layout.border')}`"
              :style="{ borderColor: PrizeManager.getRarityColor(item.rarity) }"
            >
              <div class="flex items-start gap-3">
                <div class="text-2xl">{{ PrizeManager.getItemIcon(item) }}</div>
                <div class="flex-1">
                  <h4 :class="`font-bold ${applyThemeClasses('text.primary')}`">{{ item.name }}</h4>
                  <span 
                    :style="{ color: PrizeManager.getRarityColor(item.rarity) }"
                    class="text-xs font-medium uppercase"
                  >
                    {{ item.rarity }}
                  </span>
                  <p :class="`text-sm mt-1 ${applyThemeClasses('text.secondary')}`">{{ item.description }}</p>
                  <div class="mt-2 flex justify-between items-center">
                    <span :class="`text-sm ${applyThemeClasses('text.secondary')}`">Value: {{ item.value.toLocaleString() }}G</span>
                    <button
                      :class="`text-sm px-3 py-1 rounded ${applyThemeClasses('button.success')}`"
                      @click="$emit('equip-item', item)"
                    >
                      Equip
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div 
              v-if="categorizedItems.equipment.length === 0"
              :class="`col-span-full text-center py-12 ${applyThemeClasses('text.secondary')}`"
            >
              No equipment in inventory
            </div>
          </div>

          <!-- All Items Tab -->
          <div v-if="activeTab === 'all'">
            <div class="space-y-4">
              <!-- Trophies Section -->
              <div v-if="categorizedItems.trophies.length > 0">
                <h3 :class="`text-lg font-semibold mb-2 ${applyThemeClasses('text.primary')}`">Trophies ({{ categorizedItems.trophies.length }})</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                  <div
                    v-for="trophy in categorizedItems.trophies.slice(0, 4)"
                    :key="`${trophy.month}-${trophy.name}`"
                    :class="`border rounded p-2 text-sm ${applyThemeClasses('layout.specialCard')} ${applyThemeClasses('layout.border')}`"
                  >
                    🏆 {{ trophy.name }} - {{ trophy.chocobo }}
                  </div>
                </div>
              </div>

              <!-- Consumables Section -->
              <div v-if="categorizedItems.consumables.length > 0">
                <h3 :class="`text-lg font-semibold mb-2 ${applyThemeClasses('text.primary')}`">Consumables ({{ categorizedItems.consumables.length }})</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                  <div
                    v-for="item in categorizedItems.consumables"
                    :key="item.instanceId"
                    :class="`border rounded p-2 text-sm ${applyThemeClasses('layout.card')} ${applyThemeClasses('layout.border')}`"
                  >
                    {{ PrizeManager.getItemIcon(item) }} {{ item.name }}
                  </div>
                </div>
              </div>

              <!-- Equipment Section -->
              <div v-if="categorizedItems.equipment.length > 0">
                <h3 :class="`text-lg font-semibold mb-2 ${applyThemeClasses('text.primary')}`">Equipment ({{ categorizedItems.equipment.length }})</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div
                    v-for="item in categorizedItems.equipment"
                    :key="item.instanceId"
                    :class="`border rounded p-2 text-sm ${applyThemeClasses('layout.card')} ${applyThemeClasses('layout.border')}`"
                  >
                    {{ PrizeManager.getItemIcon(item) }} {{ item.name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div 
              v-if="categorizedItems.all.length === 0"
              :class="`text-center py-12 ${applyThemeClasses('text.secondary')}`"
            >
              Your inventory is empty
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>